/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, ReactNode, useState, useCallback, useEffect, useRef } from "react";
import { getAgoricHandler } from "utils/agoric-iframe-handler";

import {
  AgoricContextType,
  FundSurveyParams,
  FundSurveyResult,
  ClaimRewardsParams,
  ClaimRewardsResult,
  SignDataParams,
  SignDataResult,
  SponsorParticipantGasParams,
  SponsorParticipantGasResult,
} from "./types";
import { LOCAL_STORAGE_KEYS } from "configs";
import { useAppDispatch } from "store/store";
import { resetAgoricProofState, setAgoricPending } from "store/zkp/agoric-proof";
import { checkAgoricProof, getProof } from "store/zkp/agoric-proof/actions";
import { SUPPORTED_AGORIC_CHAIN_ID } from "configs/web3/chains";

export const AgoricContext = createContext<AgoricContextType | null>(null);

const AgoricProvider = ({ children }: { children: ReactNode }) => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [address, setAddress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const proofHandledRef = useRef<string | null>(null);

  /**
   * NOTE: The iframe is created lazily when the first Agoric action is triggered.
   * This prevents unnecessary loading on app startup.
   */

  /**
   * Connect to Keplr wallet
   */
  const connectWallet = useCallback(async (network?: string) => {
    try {
      setIsInitializing(true);
      setError(null);

      console.log("[AgoricContext] Connecting wallet...");
      const handler = getAgoricHandler();
      const result = await handler.connectWallet(network);

      setIsConnected(true);
      setAddress(result.address);

      console.log("[AgoricContext] Wallet connected:", result.address);
      console.log("[AgoricContext] Wallet pubkey:", result.pubkey);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      console.error("[AgoricContext] Wallet connection failed:", err);
      throw new Error(errorMessage);
    } finally {
      setIsInitializing(false);
    }
  }, []);

  /**
   * Disconnect wallet
   */
  const disconnectWallet = useCallback(() => {
    setIsConnected(false);
    setAddress(null);
    setError(null);
    dispatch(resetAgoricProofState());
    proofHandledRef.current = null;

    console.log("[AgoricContext] Wallet disconnected");
  }, [dispatch]);

  /**
   * Check connection status
   */
  const checkStatus = useCallback(async () => {
    try {
      const handler = getAgoricHandler();
      const status = await handler.getStatus();
      setIsConnected(status.connected);
      setAddress(status.address);
      setError(null);
    } catch (err) {
      console.error("[AgoricContext] Failed to check status:", err);
      setError(getErrorMessage(err));
    }
  }, []);

  /**
   * Fund a survey with Agoric tokens
   */
  const fundSurvey = useCallback(
    async (params: FundSurveyParams): Promise<FundSurveyResult> => {
      try {
        setIsInitializing(true);
        setError(null);

        const handler = getAgoricHandler();
        const result = await handler.fundSurvey({
          surveyId: params.surveyId,
          messages: params.messages,
          totalAmount: params.totalAmount,
          denom: params.denom || "ubld",
        });

        setIsConnected(true);
        await checkStatus();

        console.log('[AgoricContext]', result);

        return result;
      } catch (err: unknown) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        console.error("[AgoricContext] Fund survey failed:", err);
        throw new Error(errorMessage);
      } finally {
        setIsInitializing(false);
      }
    },
    [checkStatus],
  );

  /**
   * Claim rewards from a survey
   */
  const claimRewards = useCallback(
    async (params: ClaimRewardsParams): Promise<ClaimRewardsResult> => {
      try {
        setIsInitializing(true);
        setError(null);

        const handler = getAgoricHandler();
        const result = await handler.claimRewards({
          surveyId: params.surveyId,
          messages: params.messages,
          totalAmount: params.totalAmount,
          denom: params.denom || "ubld",
        });

        setIsConnected(true);
        await checkStatus();

        return result;
      } catch (err: unknown) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        console.error("[AgoricContext] Claim rewards failed:", err);
        throw new Error(errorMessage);
      } finally {
        setIsInitializing(false);
      }
    },
    [checkStatus],
  );

  /**
   * Sign arbitrary data with wallet
   */
  const signData = useCallback(
    async (params: SignDataParams): Promise<SignDataResult> => {
      try {
        setIsInitializing(true);
        setError(null);

        const handler = getAgoricHandler();
        const result = await handler.signData({
          data: params.data,
        });

        setIsConnected(true);
        await checkStatus();

        return result;
      } catch (err: unknown) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        console.error("[AgoricContext] Sign data failed:", err);
        throw new Error(errorMessage);
      } finally {
        setIsInitializing(false);
      }
    },
    [checkStatus],
  );

  /**
   * Sponsor participant gas fees
   * Sends tokens from user's wallet to backend sponsor wallet
   */
  const sponsorParticipantGas = useCallback(
    async (params: SponsorParticipantGasParams): Promise<SponsorParticipantGasResult> => {
      try {
        setIsInitializing(true);
        setError(null);

        const handler = getAgoricHandler();
        const result = await handler.sponsorParticipantGas({
          destinationAddress: params.destinationAddress,
          amount: params.amount,
          denom: params.denom,
        });

        setIsConnected(true);
        await checkStatus();

        console.log("[AgoricContext] Gas sponsorship result:", result);

        return result;
      } catch (err: unknown) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        console.error("[AgoricContext] Sponsor participant gas failed:", err);
        throw new Error(errorMessage);
      } finally {
        setIsInitializing(false);
      }
    },
    [checkStatus],
  );

  /**
   * Handle Agoric proof verification
   */
  useEffect(() => {
    const handleAgoricProof = async () => {
      if (!isConnected || !address) {
        return;
      }

      // Check if we've already handled proof for this address
      if (proofHandledRef.current === address) {
        return;
      }

      const agoricAccessToken = localStorage.getItem(LOCAL_STORAGE_KEYS.agoricToken);
      const agoricWallet = localStorage.getItem(LOCAL_STORAGE_KEYS.agoricWallet);

      if (!agoricAccessToken || agoricWallet !== address) {
        dispatch(setAgoricPending(true));

        try {
          const res = await dispatch(getProof());

          if (!(res.payload instanceof Error)) {
            const { payload: message } = res.payload as any;

            // Sign the message using Agoric wallet
            const signedDataResult = await signData({ data: message });

            if (signedDataResult) {
              const checkRes = await dispatch(
                checkAgoricProof({
                  address: address,
                  chainId: SUPPORTED_AGORIC_CHAIN_ID,
                  pubKey: signedDataResult.signature.pub_key,
                  signature: signedDataResult.signature.signature,
                  payload: message,
                }),
              );

              dispatch(setAgoricPending(false));

              if (checkRes.payload instanceof Error) {
                console.error("[AgoricContext] Proof verification failed");
                disconnectWallet();
              } else {
                localStorage.setItem(LOCAL_STORAGE_KEYS.agoricWallet, address);
                proofHandledRef.current = address;
              }
            }
          } else {
            dispatch(setAgoricPending(false));
            console.error("[AgoricContext] Failed to get proof");
            disconnectWallet();
          }
        } catch (err) {
          console.error("[AgoricContext] Error in handleAgoricProof", err);
          dispatch(setAgoricPending(false));
          disconnectWallet();
        }
      } else {
        // Token exists and matches the wallet
        proofHandledRef.current = address;
      }
    };

    handleAgoricProof();
  }, [isConnected, address, dispatch, signData, disconnectWallet]);

  return (
    <AgoricContext.Provider
      value={{
        isInitializing,
        isConnected,
        address,
        error,
        connectWallet,
        disconnectWallet,
        fundSurvey,
        claimRewards,
        checkStatus,
        signData,
        sponsorParticipantGas,
      }}
    >
      {children}
    </AgoricContext.Provider>
  );
};

export const useAgoricContext = () => {
  const context = useContext(AgoricContext);
  if (!context) {
    throw new Error("Error in useAgoricContext");
  }
  return context;
};

/**
 * Get user-friendly error message from error object
 */
function getErrorMessage(error: unknown): string {
  // Handle error objects with code and message
  if (error && typeof error === "object" && "code" in error && "message" in error) {
    const errorWithCode = error as { code: string; message?: string };

    // Only map specific client-side error codes
    // For transaction errors, use the message from the sandbox directly
    switch (errorWithCode.code) {
      case "KEPLR_NOT_INSTALLED":
        return "Please install the Keplr wallet extension to use Agoric payments.";
      case "SANDBOX_TIMEOUT":
        return "Agoric sandbox failed to load. Please check your internet connection and try again.";
      case "IFRAME_LOAD_FAILED":
        return "Agoric sandbox could not be loaded. Please refresh the page and try again.";
      case "MESSAGE_TIMEOUT":
        return "Transaction timed out. Please check your connection and try again.";
      default:
        // For all other errors (including transaction errors), use the message directly
        return errorWithCode.message || "An error occurred during the transaction.";
    }
  }

  // Handle error messages
  if (error && typeof error === "object" && "message" in error) {
    return (error as { message: string }).message;
  }

  // Handle string errors
  if (typeof error === "string") {
    return error;
  }

  // Fallback
  return "An unexpected error occurred. Please try again.";
}

export default AgoricProvider;
